import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Drawer, Input, Modal, Space, Spin } from "antd";
import { useSelector } from "react-redux";
import { getFacilityContext } from "@/utils/route-context";
import { api } from "@/redux/api";
import { useAuth, AUTH_LEVEL } from "@/hooks/use-auth";
import ContractCompanyRelationshipTable from "@/containers/contract/ContractCompanyRelationshipTable";
import ResponsiveSelect from "@components/ResponsiveSelect";
import message from "@/utils/globalMessage";

const EXCLUDED_RELATIONSHIP_STATUSES = new Set(["pending_invited", "pending_applied", "active"]);
const getRelationshipRows = (response) => {
  const rows = response?.data?.results || response?.data || response || [];
  return Array.isArray(rows) ? rows : [];
};

export default function FacilityContractCompanyPage() {
  const context = useSelector((state) => getFacilityContext(state));
  const isPC = useSelector((state) => state.App?.isPC ?? true);
  const canWrite = useAuth("facility_contract", AUTH_LEVEL.WRITE);
  const canFullAccess = useAuth("facility_contract", AUTH_LEVEL.FULL_ACCESS);
  const [companies, setCompanies] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState();
  const [inviteMode, setInviteMode] = useState("email");
  const [inviteEmail, setInviteEmail] = useState("");
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const reloadRef = useRef();
  useEffect(() => {
    if (!canWrite || !inviteOpen || inviteMode !== "directory") return;
    setLoadingCompanies(true);
    Promise.all([
      api.getContractCompanyDirectory(),
      api.getFacilityContractRelationships(context.companyId),
    ])
      .then(([companyResponse, relationshipResponse]) => {
        const relationships = getRelationshipRows(relationshipResponse);
        const usedContractCompanyIds = new Set(
          relationships
            .filter((item) => EXCLUDED_RELATIONSHIP_STATUSES.has(item.status))
            .map((item) => String(item.contract_company ?? item.contract_company_id))
        );
        setCompanies((companyResponse.data || []).filter((item) => !usedContractCompanyIds.has(String(item.id))));
      })
      .finally(() => setLoadingCompanies(false));
  }, [canWrite, context.companyId, inviteMode, inviteOpen]);
  const submitInvite = useCallback(async () => {
    const normalizedEmail = inviteEmail.trim();
    if (!context.companyId || (inviteMode === "email" ? !normalizedEmail : !selectedCompany)) return;
    setSubmitting(true);
    try {
      if (inviteMode === "email") {
        await api.inviteContractCompany(context.companyId, null, normalizedEmail);
        message.success("Invitation email sent");
      } else {
        await api.inviteContractCompany(context.companyId, selectedCompany);
        message.success("Invitation sent");
      }
      setInviteOpen(false);
      reloadRef.current?.();
    } catch (error) {
      const responseData = error?.response?.data || {};
      const firstError = Object.values(responseData)[0];
      message.error(responseData.detail || (Array.isArray(firstError) ? firstError[0] : firstError) || "Unable to send invitation");
    } finally {
      setSubmitting(false);
    }
  }, [context.companyId, inviteEmail, inviteMode, selectedCompany]);
  const registrationLink = (() => {
    if (!context.companyId) return "";
    const params = new URLSearchParams({ company_id: String(context.companyId) });
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}/contract/register?${params.toString()}`;
  })();
  const copyRegistrationLink = useCallback(async () => {
    if (!registrationLink) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(registrationLink);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = registrationLink;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      message.success("Registration link copied");
    } catch (_) {
      message.error("Unable to copy registration link");
    }
  }, [registrationLink]);
  const inviteContent = (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      {inviteMode === "email" ? (
        <Input
          autoFocus
          type="email"
          value={inviteEmail}
          onChange={(event) => setInviteEmail(event.target.value)}
          placeholder="Contractor email"
          aria-label="Contractor email"
        />
      ) : loadingCompanies ? <Spin /> : (
        <ResponsiveSelect
          autoFocus
          style={{ width: "100%" }}
          popupTitle="Select contractor"
          placeholder="Select contractor"
          value={selectedCompany}
          options={companies.map((item) => ({ value: item.id, label: `${item.name} (${item.email})` }))}
          onChange={setSelectedCompany}
        />
      )}
    </Space>
  );
  const inviteDisabled = (inviteMode === "directory" && loadingCompanies)
    || (inviteMode === "email" ? !inviteEmail.trim() : !selectedCompany);
  const inviteActions = (
    <Space>
      <Button onClick={() => setInviteOpen(false)}>Cancel</Button>
      <Button type="primary" onClick={submitInvite} loading={submitting} disabled={inviteDisabled}>Invite</Button>
    </Space>
  );
  return (
    <>
      <ContractCompanyRelationshipTable
        mode="facility"
        title="Contractor Management"
        loadRelationships={() => api.getFacilityContractRelationships(context.companyId)}
        onCopyInvitationLink={canWrite ? copyRegistrationLink : undefined}
        onEmailInvite={canWrite ? (reload) => { reloadRef.current = reload; setInviteMode("email"); setInviteEmail(""); setInviteOpen(true); } : undefined}
        onInviteExisting={canWrite ? (reload) => { reloadRef.current = reload; setInviteMode("directory"); setSelectedCompany(undefined); setCompanies([]); setInviteOpen(true); } : undefined}
        canWrite={canWrite}
        canFullAccess={canFullAccess}
        onApprove={api.approveContractCompany}
        onReject={api.rejectFacilityContractCompany}
        onRemove={api.deleteFacilityContractCompany}
      />
      {isPC ? (
        <Modal open={inviteOpen} title={inviteMode === "email" ? "Email invitation" : "Invite existing contractor"} onCancel={() => setInviteOpen(false)} onOk={submitInvite} okButtonProps={{ disabled: inviteDisabled }} confirmLoading={submitting}>
          {inviteContent}
        </Modal>
      ) : (
        <Drawer placement="bottom" height="72vh" title={inviteMode === "email" ? "Email invitation" : "Invite existing contractor"} visible={inviteOpen} onClose={() => setInviteOpen(false)} destroyOnClose={false} className="responsive-picker-drawer" footer={inviteActions}>
          {inviteContent}
        </Drawer>
      )}
    </>
  );
}
